/**
 * @desc        Firebase DB
 * @version     1.0.1 (Fixed pump firestore polling for manual control) - 02 July 2026
 * */

import { FIREBASE_CONFIG, FIREBASE_PROXY_API } from 'app-config/index.js'
import Axios from 'axios'
import { startOfMonth, subMonths } from 'date-fns'
import { getApps as getFirebaseApps, initializeApp } from 'firebase/app'
import { browserLocalPersistence, signOut as firebaseSignOut, getAuth as getFirebaseAuth, onAuthStateChanged, setPersistence, signInWithCustomToken } from 'firebase/auth'
import { collection, deleteDoc, doc, getDocs, getFirestore, onSnapshot, orderBy, query, updateDoc, where, writeBatch } from 'firebase/firestore'
import { toast } from 'react-toastify'
import { FD_NOTIFICATIONS_SIGNIN } from 'redux/actions/action-types'
import { unsubscribeNotifications } from 'redux/actions/firebase/notification-actions'
import { store } from 'redux/store.js'

let firebaseInstance = null

export const createFirebaseInstance = () => {
    if (firebaseInstance) return firebaseInstance

    try {
        const app = getFirebaseApps().length === 0 ? initializeApp(FIREBASE_CONFIG) : getFirebaseApps()[0]
        const auth = getFirebaseAuth(app)
        setPersistence(auth, browserLocalPersistence)
        const db = getFirestore(app)

        onAuthStateChanged(auth, user => {
            store.dispatch({
                type: FD_NOTIFICATIONS_SIGNIN,
                signedIn: !!user?.uid,
            })
        })

        firebaseInstance = { app, auth, db }
        return firebaseInstance
    } catch (err) {
        console.error('Firebase DB connection failed: ', err)
        throw err
    }
}

const getFirebaseInstance = () => {
    if (!firebaseInstance) {
        firebaseInstance = createFirebaseInstance()
    }
    return firebaseInstance
}

export const getDb = () => getFirebaseInstance().db
export const getAuth = () => getFirebaseInstance().auth
export const getApp = () => getFirebaseInstance().app

export const signIn = async () => {
    try {
        const result = await Axios.get(`${FIREBASE_PROXY_API}/auth`)
        if (result && result.data && result.data.customToken) {
            const customToken = result.data.customToken
            try {
                const userCredential = await signInWithCustomToken(getAuth(), customToken)
                return !!userCredential.user
            } catch (error) {
                console.error('Error signing in with the custom token: ', error)
                throw error
            }
        } else {
            throw new Error("firebase: couldn't fetch custom token")
        }
    } catch (err) {
        console.error("firebase: couldn't authenticate the firebase ", err)
        toast.error('Error Authenticating Store - Realtime updates will not happen', {
            autoClose: 12000,
        })
    }
}

export const signOut = async () => {
    const auth = getAuth()
    if (!auth.currentUser) return

    try {
        unsubscribeNotifications()
        await firebaseSignOut(auth)
    } catch (err) {
        console.error('firebase signOut error: ', err)
        throw err
    }
}

export const addNotificationListener = (observer, user) => {
    const q = query(collection(getDb(), `users/${user}/notifications`), orderBy('timestamp', 'desc'))
    const unsubscribe = onSnapshot(q, observer, error => {
        // return a unsubscribe stub, it could fail silently
        return () => {}
    })
    return unsubscribe
}

export const addSensorsCollectionListener = (tenantId, callback) => {
    const q = query(collection(getDb(), `tenants/${tenantId}/sensors`))
    const unsubscribe = onSnapshot(
        q,
        snapshot => {
            const sensors = []
            //TODO: there's an intermittant issue here where the doc.data is undefined sometimes
            snapshot.forEach(doc => {
                const data = doc.data()
                if (data) sensors.push({ id: doc.id, ...data })
            })
            if (callback) callback(sensors)
        },
        error => {
            console.error('Error listening to sensors collection: ', error)
        },
    )
    return unsubscribe // Call this function to stop listening to changes
}

export const addEntitiesCollectionListener = (tenantId, callback) => {
    const q = query(collection(getDb(), `tenants/${tenantId}/entities`))
    const unsubscribe = onSnapshot(
        q,
        snapshot => {
            const entities = []
            snapshot.forEach(doc => {
                const data = doc.data()
                if (data) entities.push({ id: doc.id, ...data })
            })

            if (callback) callback(entities)
        },
        error => {
            console.error('Error listening to entities collection: ', error)
        },
    )
    return unsubscribe // Call this function to stop listening to changes
}

export const deleteSensorsDocNotification = async props => {
    const { id, tenantId, sensorId } = props
    if (!getDb() || !id || !tenantId) return null // Ensure all required parameters are provided
    deleteDoc(doc(getDb(), `tenants/${tenantId}/sensors`, sensorId))
        .then(e => {
            // console.log(`NOTIFICATION ${id} successfully deleted`)
        })
        .catch(error => {
            // The document probably doesn't exist, or there was another error.
            console.error('Error deleting notification: ', error)
        })
}

export const arcNotification = async props => {
    const { docId, userName } = props
    if (!getDb() || !docId || !userName) return null

    try {
        await updateDoc(doc(getDb(), `users/${userName}/notifications`, docId), {
            isArchived: true,
        })
    } catch (error) {
        // The document probably doesn't exist.
        console.error('Error archiving notification: ', error)
    }
}

export const redNotification = async props => {
    const { docId, userName } = props
    if (!getDb() || !docId || !userName) return null
    try {
        await updateDoc(doc(getDb(), `users/${userName}/notifications`, docId), {
            isRead: true,
            isNew: false,
        })
    } catch (error) {
        // The document probably doesn't exist.
        console.error('Error reding notification: ', error)
    }
}

export const deleteMultipleNotifications = async userName => {
    if (!getDb() || !userName) return null
    const oneMonthAgo = startOfMonth(subMonths(new Date(), 1)).getTime()

    try {
        const userId = getAuth().currentUser?.uid
        if (!userId) {
            return Promise.resolve('No user found')
        }
        const q = query(collection(getDb(), `users/${userId}/notifications`), where('timestamp', '<', oneMonthAgo))
        const querySnapshot = await getDocs(q)
        if (querySnapshot.empty) {
            return Promise.resolve('No notifications to delete')
        }

        const batch = writeBatch(getDb())

        querySnapshot.forEach(doc => {
            batch.delete(doc.ref)
        })

        await batch.commit()
        return Promise.resolve('Old notifications deleted successfully')
    } catch (error) {
        return Promise.reject('Error deleting notifications: ', error)
    }
}
